/**
 * RecordingCompositor — one continuous recording video track drawn on a canvas:
 *
 *   first FACE_SECONDS of the recording → the candidate's camera, full frame
 *   afterwards                           → the shared screen, with a small live
 *                                          camera bubble in the bottom-right corner
 *
 * A MediaRecorder can't switch its source mid-file, so the recorder records the
 * canvas instead and this class decides what to draw on every frame. Falls back
 * to the camera whenever the screen share is missing or has ended (and to the
 * screen alone if the camera stops), so the recording never goes black.
 *
 * Frames are driven by a Web Worker timer: main-thread timers and
 * requestAnimationFrame are paused or heavily throttled when the tab is hidden,
 * which would freeze the recording exactly when proctoring cares most.
 */

const FACE_SECONDS = 60;
const FPS = 12;
const WIDTH = 1280;
const HEIGHT = 720;
const BUBBLE_WIDTH = 0.2; // of canvas width
const BUBBLE_MARGIN = 20;

function hasFrame(video) {
  return !!video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
}

function isLive(stream) {
  return !!stream && stream.getVideoTracks().some((t) => t.readyState === 'live');
}

export default class RecordingCompositor {
  /**
   * @param {HTMLVideoElement} cameraVideo - playing <video> with the candidate's camera
   * @param {{faceSeconds?: number, fps?: number}} [options]
   */
  constructor(cameraVideo, { faceSeconds = FACE_SECONDS, fps = FPS } = {}) {
    this.cameraVideo = cameraVideo;
    this.faceMs = faceSeconds * 1000;
    this.fps = fps;
    this.recordingStartedAt = null;
    this.screenStream = null;

    this.canvas = document.createElement('canvas');
    this.canvas.width = WIDTH;
    this.canvas.height = HEIGHT;
    this.ctx = this.canvas.getContext('2d', { alpha: false });

    this.screenVideo = document.createElement('video');
    this.screenVideo.muted = true;
    this.screenVideo.playsInline = true;
    this.screenVideo.setAttribute('playsinline', '');

    this._timer = null;
    this._worker = null;
    this.stream = this.canvas.captureStream(fps);
    this._draw(); // a first frame so the track is never empty
    this._startTicker();
  }

  /** The composited video track to record (and publish to live monitoring). */
  get videoTrack() {
    return this.stream.getVideoTracks()[0];
  }

  /** Point the "screen" phase at the current screen share (call again after a re-share). */
  setScreenStream(stream) {
    if (stream === this.screenStream) return;
    this.screenStream = stream || null;
    this.screenVideo.srcObject = this.screenStream;
    if (this.screenStream) this.screenVideo.play().catch(() => {});
  }

  /** Called when the MediaRecorder actually starts: the face phase is the first minute of the recording. */
  markRecordingStart() {
    if (this.recordingStartedAt === null) this.recordingStartedAt = Date.now();
  }

  /** "face" during the intro (or before recording starts), then "screen". */
  get phase() {
    if (this.recordingStartedAt === null) return 'face';
    return Date.now() - this.recordingStartedAt < this.faceMs ? 'face' : 'screen';
  }

  stop() {
    if (this._worker) {
      this._worker.terminate();
      this._worker = null;
    }
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
    this.stream.getTracks().forEach((t) => t.stop());
    this.screenVideo.srcObject = null;
  }

  _startTicker() {
    const interval = Math.round(1000 / this.fps);
    try {
      const src = `setInterval(() => postMessage(0), ${interval});`;
      const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
      this._worker = new Worker(url);
      URL.revokeObjectURL(url);
      this._worker.onmessage = () => this._draw();
    } catch (e) {
      // Workers unavailable (very old browser / CSP): main-thread timer fallback.
      this._timer = setInterval(() => this._draw(), interval);
    }
  }

  _draw() {
    const { ctx } = this;
    const camera = hasFrame(this.cameraVideo) ? this.cameraVideo : null;
    const screen = isLive(this.screenStream) && hasFrame(this.screenVideo) ? this.screenVideo : null;
    try {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      if (this.phase === 'screen' && screen) {
        this._drawFit(screen, 0, 0, WIDTH, HEIGHT, false);
        if (camera) this._drawBubble(camera);
      } else if (camera) {
        this._drawFit(camera, 0, 0, WIDTH, HEIGHT, true);
      } else if (screen) {
        this._drawFit(screen, 0, 0, WIDTH, HEIGHT, false);
      }
    } catch (e) {
      // A source can be torn down mid-frame (re-share, device change); skip this frame.
    }
  }

  /** cover=true fills the box (crops); cover=false fits inside it (letterbox — keeps the whole screen readable). */
  _drawFit(video, x, y, w, h, cover) {
    const vr = video.videoWidth / video.videoHeight;
    const br = w / h;
    let dw = w;
    let dh = h;
    if (cover ? vr > br : vr < br) {
      dw = h * vr;
    } else {
      dh = w / vr;
    }
    this.ctx.drawImage(video, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  }

  _drawBubble(camera) {
    const { ctx } = this;
    const w = Math.round(WIDTH * BUBBLE_WIDTH);
    const h = Math.round((w * 3) / 4);
    const x = WIDTH - w - BUBBLE_MARGIN;
    const y = HEIGHT - h - BUBBLE_MARGIN;
    const r = 12;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.clip();
    this._drawFit(camera, x, y, w, h, true);
    ctx.restore();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.stroke();
  }
}

export { FACE_SECONDS };
